/**
 * Initiative Breakdown API
 *
 * Client for the FLOW-545/546/549 breakdown routes: negotiate and commit.
 * Generation (FLOW-881) is asynchronous: `POST /intakes/{id}/breakdown`
 * returns 202 immediately and the backend fills the row in the background,
 * so `generateBreakdown` no longer holds a connection open for the
 * Anthropic round trip — it posts, then polls `GET .../breakdown` via
 * `waitForBreakdown` until the row is ready. Negotiate and commit are still
 * synchronous AI round trips and keep the chat timeout.
 */

import { api, ApiError } from "./client";
import type {
  BreakdownGenerationStatus,
  InitiativeBreakdown,
  IntakeId,
  WorkItem,
} from "./types";

/** Matches the chat client: the backend's Anthropic timeout plus headroom */
const BREAKDOWN_TIMEOUT_MS = 130_000;

/** A negotiation turn returns the revised breakdown alongside the reply to show the user */
export interface BreakdownTurnResponse {
  breakdown: InitiativeBreakdown;
  reply: string;
}

export interface CommitBreakdownResponse {
  /** One per approved group; empty when every group placed its stories at the top level */
  epics: WorkItem[];
  stories: WorkItem[];
}

/**
 * Run a request under the breakdown timeout, converting an abort into a 408.
 * Exported for other AI round-trip endpoints (e.g. `workItems.ts`'s
 * `proposeChildren`) that share the same backend timeout.
 */
export async function withTimeout<T>(
  run: (signal: AbortSignal) => Promise<T>,
): Promise<T> {
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), BREAKDOWN_TIMEOUT_MS);

  try {
    return await run(controller.signal);
  } catch (err) {
    if (err instanceof DOMException && err.name === "AbortError") {
      throw new ApiError(408, "The breakdown request timed out. Please retry.");
    }
    throw err;
  } finally {
    clearTimeout(timeout);
  }
}

/** Poll delays: start at 1s, back off ×1.5, cap at 5s */
const POLL_INITIAL_MS = 1000;
const POLL_MAX_MS = 5000;
/** Give up waiting for generation after 180s */
const POLL_DEADLINE_MS = 180_000;

/** The row once generation finishes, or a signal that it's still running */
export type BreakdownLookup =
  | { status: "ready"; breakdown: InitiativeBreakdown }
  | { status: "generating" };

/**
 * Fetch the current breakdown for an intake. A 202 means generation is
 * still running; a 404 ApiError means none exists yet (thrown, not part of
 * the union — callers that need "no breakdown yet" catch it explicitly).
 */
export async function getBreakdown(
  intakeId: IntakeId,
): Promise<BreakdownLookup> {
  const { data, status } = await api.getWithStatus<
    InitiativeBreakdown | BreakdownGenerationStatus
  >(`/intakes/${intakeId}/breakdown`);
  return status === 202
    ? { status: "generating" }
    : { status: "ready", breakdown: data as InitiativeBreakdown };
}

/**
 * Poll until a running generation finishes. A 404 mid-poll means the
 * generation job vanished (backend restart, or the intake/breakdown was
 * discarded mid-flight) — surfaced as a 502 rather than the raw 404 so it
 * doesn't read as "no breakdown was ever requested".
 */
export async function waitForBreakdown(
  intakeId: IntakeId,
): Promise<InitiativeBreakdown> {
  const started = Date.now();
  let delay = POLL_INITIAL_MS;

  for (;;) {
    await new Promise((resolve) => setTimeout(resolve, delay));

    let lookup: BreakdownLookup;
    try {
      lookup = await getBreakdown(intakeId);
    } catch (err) {
      if (err instanceof ApiError && err.status === 404) {
        throw new ApiError(
          502,
          "Generation was interrupted. Please try again.",
        );
      }
      throw err;
    }

    if (lookup.status === "ready") return lookup.breakdown;

    if (Date.now() - started > POLL_DEADLINE_MS) {
      throw new ApiError(408, "The breakdown request timed out. Please retry.");
    }

    delay = Math.min(delay * 1.5, POLL_MAX_MS);
  }
}

/** Generate a fresh breakdown from the intake's assembled spec */
export async function generateBreakdown(
  intakeId: IntakeId,
): Promise<InitiativeBreakdown> {
  await api.post<BreakdownGenerationStatus>(
    `/intakes/${intakeId}/breakdown`,
    undefined,
  );
  return waitForBreakdown(intakeId);
}

/**
 * Throw the breakdown away (FLOW-641). The intake's spec is untouched, so the
 * surface goes back to offering "Generate breakdown". Like `getBreakdown`,
 * this runs no AI round trip and so takes the default fetch behaviour.
 */
export async function discardBreakdown(intakeId: IntakeId): Promise<void> {
  return api.delete(`/intakes/${intakeId}/breakdown`);
}

/** Negotiate a revision — add, remove, split, or reframe stories conversationally */
export async function sendBreakdownTurn(
  intakeId: IntakeId,
  content: string,
  rejectedStoryIds: string[],
): Promise<BreakdownTurnResponse> {
  return withTimeout((signal) =>
    api.post<BreakdownTurnResponse>(
      `/intakes/${intakeId}/breakdown/messages`,
      { content, rejected_story_ids: rejectedStoryIds },
      { signal },
    ),
  );
}

/** Commit the approved stories, creating the epic (if new) and its children */
export async function commitBreakdown(
  intakeId: IntakeId,
  storyIds: string[],
): Promise<CommitBreakdownResponse> {
  return withTimeout((signal) =>
    api.post<CommitBreakdownResponse>(
      `/intakes/${intakeId}/breakdown/commit`,
      { story_ids: storyIds },
      { signal },
    ),
  );
}
